import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { adminApi } from '../utils/api'
import { formatProbability, formatTimeAgo } from '../utils/format'
import clsx from 'clsx'

type Tab = 'working' | 'episodes' | 'contrastive' | 'agents'

export function Memory() {
  const [activeTab, setActiveTab] = useState<Tab>('episodes')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [outcomeFilter, setOutcomeFilter] = useState<string>('')
  const [workingTypeFilter, setWorkingTypeFilter] = useState<string>('')

  const [episodeOffset, setEpisodeOffset] = useState(0)
  const [allEpisodes, setAllEpisodes] = useState<Array<{
    id: number
    question_id: number
    question_text: string
    category: string | null
    prediction_prob: number | null
    actual_outcome: number | null
    brier_score: number | null
    key_insights: string[]
    created_at: string | null
  }>>([])

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['memory', categoryFilter, episodeOffset],
    queryFn: () => adminApi.memory(categoryFilter || undefined, episodeOffset),
    refetchInterval: 60000,
  })

  // Accumulate episodes when offset changes
  const episodes = episodeOffset === 0 ? (data?.episodes ?? []) : [...allEpisodes, ...(data?.episodes ?? [])]

  const loadMore = () => {
    if (data?.has_more) {
      setAllEpisodes(episodes)
      setEpisodeOffset((prev) => prev + 50)
    }
  }

  // Reset accumulated episodes when category changes
  const handleCategoryChange = (newCategory: string) => {
    setEpisodeOffset(0)
    setAllEpisodes([])
    setCategoryFilter(newCategory)
  }

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'working', label: 'Working Memory', icon: '🧠' },
    { id: 'episodes', label: 'Past Predictions', icon: '📚' },
    { id: 'contrastive', label: 'Contrastive', icon: '⚖️' },
    { id: 'agents', label: 'Agent Performance', icon: '🤖' },
  ]

  const filteredEpisodes = episodes.filter((ep) => {
    if (outcomeFilter === 'success' && (ep.brier_score === null || ep.brier_score >= 0.1)) return false
    if (outcomeFilter === 'failure' && (ep.brier_score === null || ep.brier_score <= 0.25)) return false
    return true
  })

  const filteredWorkingEntries = data?.working_entries.filter((entry) => {
    if (workingTypeFilter && entry.type !== workingTypeFilter) return false
    return true
  }) ?? []

  // Get unique working memory types for filter
  const workingMemoryTypes = [...new Set(data?.working_entries.map((e) => e.type) ?? [])]

  const getBrierColor = (brier: number | null) => {
    if (brier === null) return 'text-star-muted'
    if (brier < 0.1) return 'text-signal-green'
    if (brier > 0.25) return 'text-signal-red'
    return 'text-signal-amber'
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-display font-semibold text-star mb-2">
            Memory Explorer
          </h2>
          <p className="text-star-muted text-sm">
            Episodic memory, working context, and learning insights
          </p>
        </div>
        <button className="btn-secondary" onClick={() => refetch()}>
          ↻ Refresh
        </button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border-2 border-signal-purple/30 border-t-signal-purple animate-spin"></div>
            <p className="text-star-muted font-mono text-sm">Loading memory...</p>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="glass-card p-8 text-center">
          <p className="text-signal-red font-mono mb-4">Failed to load memory</p>
          <button className="btn-primary" onClick={() => refetch()}>
            Try Again
          </button>
        </div>
      )}

      {data && (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="glass-card p-4 text-center">
              <div className="text-2xl font-mono font-semibold text-signal-blue">
                {data.stats.working_total}
              </div>
              <div className="text-xs text-star-muted mt-1">Working Memory</div>
            </div>
            <div className="glass-card p-4 text-center">
              <div className="text-2xl font-mono font-semibold text-signal-purple">
                {data.stats.total_entries}
              </div>
              <div className="text-xs text-star-muted mt-1">Episodic Memory</div>
            </div>
            <div className="glass-card p-4 text-center">
              <div className={clsx(
                'text-2xl font-mono font-semibold',
                getBrierColor(data.stats.avg_brier)
              )}>
                {data.stats.avg_brier?.toFixed(3) ?? '—'}
              </div>
              <div className="text-xs text-star-muted mt-1">Avg Brier (resolved)</div>
            </div>
            <div className="glass-card p-4 text-center">
              <div className="text-2xl font-mono font-semibold text-signal-amber">
                {data.stats.working_added_24h}
              </div>
              <div className="text-xs text-star-muted mt-1">Added (24h)</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={clsx(
                  'px-4 py-2 rounded-lg font-mono text-sm transition-all whitespace-nowrap',
                  activeTab === tab.id
                    ? 'bg-signal-purple/10 text-signal-purple border border-signal-purple/30'
                    : 'text-star-muted hover:text-star hover:bg-void-100 border border-transparent'
                )}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Working Memory Tab */}
          <AnimatePresence mode="wait">
            {activeTab === 'working' && (
              <motion.div
                key="working"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="glass-card p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <p className="text-sm text-star-muted">
                      Working memory stores searches, facts, and reasoning discovered during prediction runs.
                    </p>
                    {workingMemoryTypes.length > 0 && (
                      <select
                        className="input w-auto"
                        value={workingTypeFilter}
                        onChange={(e) => setWorkingTypeFilter(e.target.value)}
                      >
                        <option value="">All Types</option>
                        {workingMemoryTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {filteredWorkingEntries.length > 0 ? (
                    <div className="grid gap-4">
                      {filteredWorkingEntries.map((entry, index) => (
                        <motion.div
                          key={entry.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.03 }}
                          className="p-4 rounded-lg bg-void-50 border border-star-dim/20 hover:border-signal-purple/30 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={clsx(
                                'px-2 py-0.5 rounded text-xs font-mono',
                                entry.type === 'search_result' ? 'bg-signal-blue/10 text-signal-blue' :
                                entry.type === 'reasoning' ? 'bg-signal-purple/10 text-signal-purple' :
                                entry.type === 'fact' ? 'bg-signal-green/10 text-signal-green' :
                                'bg-void-100 text-star-muted'
                              )}>
                                {entry.type}
                              </span>
                              {entry.reliability > 0.7 && (
                                <span className="text-[10px] text-signal-green font-mono">HIGH RELIABILITY</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-star-dim font-mono">
                              <span>Weight: {entry.weight.toFixed(2)}</span>
                              <span>{entry.age_hours.toFixed(0)}h ago</span>
                            </div>
                          </div>
                          <p className="text-sm text-star">{entry.content}</p>
                          {entry.source_domain && (
                            <div className="mt-2 text-xs text-signal-blue">
                              {entry.source_domain}
                            </div>
                          )}
                          {entry.topics.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {entry.topics.map((topic, i) => (
                                <span key={i} className="px-1.5 py-0.5 rounded bg-void-100 text-[10px] text-star-dim">
                                  {topic}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="text-[10px] text-star-dim mt-2">
                            Accessed {entry.access_count}x • Reliability: {(entry.reliability * 100).toFixed(0)}%
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-star-muted">No working memory entries yet</p>
                      <p className="text-star-dim text-sm mt-2">Run predictions to start building working memory</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Episodes Tab */}
            {activeTab === 'episodes' && (
              <motion.div
                key="episodes"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="glass-card p-6">
                  {/* Filters */}
                  <div className="flex gap-4 mb-6">
                    <select
                      className="input w-auto"
                      value={categoryFilter}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {data.categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <select
                      className="input w-auto"
                      value={outcomeFilter}
                      onChange={(e) => setOutcomeFilter(e.target.value)}
                    >
                      <option value="">All Outcomes</option>
                      <option value="success">Successes (Brier &lt; 0.1)</option>
                      <option value="failure">Failures (Brier &gt; 0.25)</option>
                    </select>
                  </div>

                  {filteredEpisodes.length > 0 ? (<>
                    <div className="grid gap-4">
                      {filteredEpisodes.map((episode, index) => (
                        <motion.div
                          key={episode.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                        >
                          <Link
                            to={`/question/${episode.question_id}`}
                            className="block p-4 rounded-lg bg-void-50 border border-star-dim/20 hover:border-signal-purple/40 hover:bg-void-100/50 transition-colors"
                          >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 mr-4">
                              <p className="text-sm text-star font-medium line-clamp-2 hover:text-signal-purple transition-colors">
                                {episode.question_text}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={clsx(
                                'px-2 py-1 rounded font-mono text-xs',
                                episode.brier_score !== null && episode.brier_score < 0.1
                                  ? 'bg-signal-green/10 text-signal-green border border-signal-green/30'
                                  : episode.brier_score !== null && episode.brier_score > 0.25
                                  ? 'bg-signal-red/10 text-signal-red border border-signal-red/30'
                                  : 'bg-void-100 text-star-muted'
                              )}>
                                Pred: {formatProbability(episode.prediction_prob)}
                              </span>
                              {episode.actual_outcome !== null && (
                                <span className={clsx(
                                  'px-2 py-1 rounded font-mono text-xs',
                                  episode.actual_outcome > 0.5
                                    ? 'bg-signal-green/20 text-signal-green'
                                    : 'bg-signal-red/20 text-signal-red'
                                )}>
                                  {episode.actual_outcome > 0.5 ? 'YES' : 'NO'}
                                </span>
                              )}
                            </div>
                          </div>

                          {episode.brier_score !== null && (
                            <div className="font-mono text-xs text-star-muted mb-2">
                              Brier: <span className={getBrierColor(episode.brier_score)}>
                                {episode.brier_score.toFixed(3)}
                              </span>
                            </div>
                          )}

                          {episode.key_insights.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-star-dim/10">
                              <span className="text-signal-amber text-xs font-semibold">Lesson: </span>
                              <span className="text-sm text-star-muted">{episode.key_insights[0]}</span>
                            </div>
                          )}

                          <div className="flex items-center gap-2 mt-3 text-[10px] text-star-dim">
                            {episode.category && <span>{episode.category}</span>}
                            {episode.created_at && (
                              <>
                                <span>•</span>
                                <span>{formatTimeAgo(episode.created_at)}</span>
                              </>
                            )}
                            <span className="ml-auto text-signal-purple/60">View →</span>
                          </div>
                          </Link>
                        </motion.div>
                      ))}
                    </div>

                    {/* Load More button */}
                    {data.has_more && (
                      <div className="mt-6 text-center">
                        <button
                          className="btn-secondary"
                          onClick={loadMore}
                          disabled={isFetching}
                        >
                          {isFetching ? (
                            <>
                              <span className="inline-block w-4 h-4 border-2 border-signal-purple/30 border-t-signal-purple rounded-full animate-spin mr-2"></span>
                              Loading...
                            </>
                          ) : (
                            <>Load More Episodes</>
                          )}
                        </button>
                        <p className="text-xs text-star-dim mt-2">
                          Showing {filteredEpisodes.length} episodes
                        </p>
                      </div>
                    )}
                  </>) : (
                    <div className="text-center py-12">
                      <p className="text-star-muted">No prediction episodes in memory yet</p>
                      <p className="text-star-dim text-sm mt-2">Run predictions and wait for resolutions to build memory</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Contrastive Tab */}
            {activeTab === 'contrastive' && (
              <motion.div
                key="contrastive"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {data.contrast_insight && (
                  <div className="glass-card p-6 mb-6 bg-gradient-to-r from-signal-purple/5 to-signal-blue/5 border-signal-purple/30">
                    <div className="text-xs text-signal-purple font-mono uppercase tracking-wider mb-2">
                      Key Pattern
                    </div>
                    <p className="text-star">{data.contrast_insight}</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Successes */}
                  <div className="glass-card p-6">
                    <h3 className="text-signal-green font-display font-semibold mb-4 flex items-center gap-2">
                      <span>Successful Predictions</span>
                    </h3>
                    {data.successes.length > 0 ? (
                      <div className="space-y-4">
                        {data.successes.map((s) => (
                          <div key={s.id} className="p-3 rounded-lg bg-signal-green/5 border border-signal-green/20">
                            <p className="text-sm text-star line-clamp-2">{s.question_text}</p>
                            <div className="mt-2 font-mono text-xs text-star-muted">
                              Pred: {formatProbability(s.prediction_prob)} →
                              <span className="text-signal-green ml-1">Brier: {s.brier_score?.toFixed(3)}</span>
                            </div>
                            {s.key_insights[0] && (
                              <div className="mt-2 text-xs text-star-dim">{s.key_insights[0]}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-star-muted text-sm text-center py-8">No successes recorded yet</p>
                    )}
                  </div>

                  {/* Failures */}
                  <div className="glass-card p-6">
                    <h3 className="text-signal-red font-display font-semibold mb-4 flex items-center gap-2">
                      <span>Failed Predictions</span>
                    </h3>
                    {data.failures.length > 0 ? (
                      <div className="space-y-4">
                        {data.failures.map((f) => (
                          <div key={f.id} className="p-3 rounded-lg bg-signal-red/5 border border-signal-red/20">
                            <p className="text-sm text-star line-clamp-2">{f.question_text}</p>
                            <div className="mt-2 font-mono text-xs text-star-muted">
                              Pred: {formatProbability(f.prediction_prob)} →
                              <span className="text-signal-red ml-1">Brier: {f.brier_score?.toFixed(3)}</span>
                            </div>
                            {f.key_insights[0] && (
                              <div className="mt-2 text-xs text-star-dim">{f.key_insights[0]}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-star-muted text-sm text-center py-8">No failures recorded yet</p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Agents Tab */}
            {activeTab === 'agents' && (
              <motion.div
                key="agents"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.values(data.agent_performance).length > 0 ? (
                    Object.values(data.agent_performance).map((agent, index) => (
                      <motion.div
                        key={agent.agent_name}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="glass-card p-5"
                      >
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-xl">
                            {agent.agent_name === 'news' ? '📰' :
                             agent.agent_name === 'quant' ? '📊' :
                             agent.agent_name === 'skeptic' ? '🔍' :
                             agent.agent_name === 'synthesis' ? '🔮' :
                             agent.agent_name === 'baserate' ? '📈' :
                             agent.agent_name === 'planner' ? '📋' : '🤖'}
                          </span>
                          <span className="font-display font-semibold text-star capitalize">
                            {agent.agent_name}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-center">
                          <div>
                            <div className="text-xl font-mono font-semibold text-star">
                              {agent.samples}
                            </div>
                            <div className="text-[10px] text-star-dim uppercase">Samples</div>
                          </div>
                          <div>
                            <div className={clsx(
                              'text-xl font-mono font-semibold',
                              agent.avg_brier !== null
                                ? agent.avg_brier < 0.15
                                  ? 'text-signal-green'
                                  : agent.avg_brier < 0.25
                                  ? 'text-signal-amber'
                                  : 'text-signal-red'
                                : 'text-star-muted'
                            )}>
                              {agent.avg_brier?.toFixed(3) ?? 'N/A'}
                            </div>
                            <div className="text-[10px] text-star-dim uppercase">Avg Brier</div>
                          </div>
                        </div>

                        {agent.recent_trend && (
                          <div className="mt-4 text-center">
                            <span className={clsx(
                              'px-2 py-1 rounded text-xs font-mono',
                              agent.recent_trend === 'improving' ? 'bg-signal-green/10 text-signal-green' :
                              agent.recent_trend === 'declining' ? 'bg-signal-red/10 text-signal-red' :
                              'bg-void-100 text-star-muted'
                            )}>
                              {agent.recent_trend === 'improving' ? '📈 ' : agent.recent_trend === 'declining' ? '📉 ' : '➡️ '}
                              {agent.recent_trend}
                            </span>
                          </div>
                        )}
                      </motion.div>
                    ))
                  ) : (
                    <div className="col-span-full glass-card p-12 text-center">
                      <p className="text-star-muted">No agent performance data yet</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  )
}
